// =============================================================
// Supervision des clients : MC relit l'historique des conversations de chaque
// instance client (prod et préprod) par son export en lecture seule. Le
// contrat distant est celui du module x-appels des SaaS clients (version 1),
// documenté dans MC-Blueprint, docs/voix/GESTION-CLIENTS-VOIX.md, section 7.
// =============================================================

export type MonitorEnvironment = "prod" | "preprod";

/** Une source = un client × un environnement, telle que l'API la renvoie (jeton jamais inclus). */
export interface MonitorSource {
  id: string;
  clientName: string;
  clientSlug: string;
  environment: MonitorEnvironment;
  baseUrl: string;
  /** Quatre derniers caractères du jeton, pour le reconnaître. */
  tokenHint: string;
  phoneNumber: string | null;
  testNumbers: string[];
  enabled: boolean;
  position: number;
  createdAt: number | string;
  updatedAt: number | string;
}

// ---- Contrat distant (x-appels, version 1) ----

export type RemoteCallStatus = "en_cours" | "termine" | "manque" | "transfere";
export type RemoteCallOutcome =
  | "rdv_pris"
  | "message"
  | "renseignement"
  | "transfert"
  | "rappel"
  | "aucun";
export type RemoteCallOrigin = "telephone" | "simulation";

export interface RemoteTranscriptLine {
  at: number;
  speaker: "ia" | "client";
  text: string;
}

export interface RemoteProvenance {
  engine: string | null;
  appRevision: string | null;
  engineRevision: string | null;
  profile: string | null;
  fastModel: string | null;
  smartModel: string | null;
  voice: string | null;
  preparedTurns: number | null;
  modelCalls: number | null;
  modelMs: number | null;
}

export interface RemoteSourceInfo {
  client: string;
  environment: MonitorEnvironment;
  deployEnv: string | null;
  phoneNumber: string | null;
  generatedAt: string;
}

export interface RemoteStats {
  today: number;
  week: number;
  inProgress: number;
  total: number;
  lastCallAt: string | null;
}

export interface RemoteCallSummary {
  id: string;
  providerId: string | null;
  direction: "entrant" | "sortant";
  status: RemoteCallStatus;
  outcome: RemoteCallOutcome;
  origin: RemoteCallOrigin;
  provenance: RemoteProvenance | null;
  fromNumber: string;
  toNumber: string | null;
  callerName: string | null;
  clientId: string | null;
  appointmentId: string | null;
  startedAt: string;
  endedAt: string | null;
  durationSec: number;
  intent: string | null;
  summary: string | null;
  vehicle: string | null;
  service: string | null;
  requestedAt: string | null;
  recordingUrl: string | null;
  notes: string | null;
  handled: boolean;
  turns: number;
  clientTurns: number;
  createdAt: string;
  updatedAt: string;
}

export type RemoteCall = Omit<RemoteCallSummary, "turns" | "clientTurns"> & {
  transcript: RemoteTranscriptLine[];
};

// ---- Ce que l'API MC renvoie au tableau de bord ----

export type SourceHealthState =
  | "ok"
  | "unreachable"
  | "unauthorized"
  | "not_configured"
  | "bad_contract"
  | "error";

export interface SourceHealth {
  state: SourceHealthState;
  /** Explication courte, lisible, sans jeton ni corps de réponse. */
  message: string | null;
  latencyMs: number | null;
  checkedAt: string;
  remote: RemoteSourceInfo | null;
  stats: RemoteStats | null;
  /** L'instance se déclare dans un autre environnement que celui configuré ici. */
  environmentMismatch: boolean;
}

export interface SourceOverview {
  source: MonitorSource;
  health: SourceHealth;
}

/** Fiche de liste enrichie par MC : test interne si le numéro appelant est un testeur déclaré. */
export type MonitoredCallSummary = RemoteCallSummary & { internalTest: boolean };
export type MonitoredCall = RemoteCall & { internalTest: boolean };

export interface MonitoredCallsPage {
  source: MonitorSource;
  remote: RemoteSourceInfo;
  calls: MonitoredCallSummary[];
  hasMore: boolean;
}

export interface MonitoredCallDetail {
  source: MonitorSource;
  remote: RemoteSourceInfo;
  call: MonitoredCall;
}

export interface MonitoredCallsQuery {
  since?: string;
  before?: string;
  limit?: number;
  origin?: RemoteCallOrigin;
  status?: RemoteCallStatus[];
  outcome?: RemoteCallOutcome[];
  q?: string;
}

// ---- Libellés ----

export const MONITOR_ENVIRONMENT_LABELS: Record<MonitorEnvironment, string> = {
  prod: "Production",
  preprod: "Préproduction",
};

export const MONITOR_ENVIRONMENT_HINTS: Record<MonitorEnvironment, string> = {
  prod: "Clients réels",
  preprod: "Tests",
};

export const REMOTE_STATUS_LABELS: Record<RemoteCallStatus, string> = {
  en_cours: "En cours",
  termine: "Terminé",
  manque: "Manqué",
  transfere: "Transféré",
};

export const REMOTE_OUTCOME_LABELS: Record<RemoteCallOutcome, string> = {
  rdv_pris: "RDV pris",
  message: "Message",
  renseignement: "Renseignement",
  transfert: "Transféré",
  rappel: "À rappeler",
  aucun: "Sans suite",
};

export const REMOTE_ORIGIN_LABELS: Record<RemoteCallOrigin, string> = {
  telephone: "Téléphone",
  simulation: "Simulation",
};

export const SOURCE_HEALTH_LABELS: Record<SourceHealthState, string> = {
  ok: "Joignable",
  unreachable: "Injoignable",
  unauthorized: "Jeton refusé",
  not_configured: "Jeton absent côté client",
  bad_contract: "Réponse inattendue",
  error: "Erreur",
};
